import { Link, useParams } from 'react-router-dom';
import EmptyState from '@/components/base/EmptyState';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeader from '@/components/feature/SectionHeader';
import { getActorById, getMoviesByActor } from '@/lib/catalog';

export default function ActorPage() {
  const { id } = useParams();
  const actor = getActorById(id);

  if (!actor) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-user-search-line"
          title="未找到相关信息"
          description="这位演员的资料暂未收录，或链接有误。返回首页看看其他影视作品吧。"
          action={
            <Link
              to="/"
              className="rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const works = getMoviesByActor(actor.id);

  const infoRows = [
    { label: '性别', value: actor.gender },
    { label: '生日', value: actor.birthday },
    { label: '出生地', value: actor.birthplace },
    { label: '代表作品', value: `${works.length} 部` },
  ];

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 to-primary-950" />
        <div className="absolute -right-10 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-accent-500/10 blur-2xl" />
        <div className="relative mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
          <nav aria-label="面包屑导航" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-background-300">
            <Link to="/" className="transition-colors hover:text-accent-400 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-background-100">演员资料</span>
          </nav>

          <div className="flex flex-col items-center gap-6 md:flex-row md:items-end">
            <div className="h-32 w-32 flex-shrink-0 overflow-hidden rounded-full border-4 border-primary-800 md:h-40 md:w-40">
              <img
                src={actor.avatar}
                alt={`${actor.name} 照片`}
                title={`${actor.name} 演员资料`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="text-center md:text-left">
              <h1 className="font-heading text-3xl font-black text-background-50 md:text-4xl">
                {actor.name}
              </h1>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 font-label text-sm text-background-200 md:justify-start">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" aria-hidden="true" />
                  {actor.gender}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" aria-hidden="true" />
                  {actor.birthday}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line" aria-hidden="true" />
                  {actor.birthplace}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: '演员资料' }, { label: actor.name }]} />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
          <section>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
              演员简介
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">{actor.bio}</p>
          </section>

          <aside>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">个人资料</h2>
              <dl className="mt-4 flex flex-col gap-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-20 flex-shrink-0 font-label text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <SectionHeader
            title="代表作品"
            subtitle={`${actor.name} 参演的影视作品，点击查看详情`}
          />
          {works.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {works.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-film-line"
              title="暂无代表作品"
              description="这位演员的作品资料还在整理中。"
            />
          )}
        </section>
      </div>
    </div>
  );
}